import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { postsAPI } from '../services/api';
import { 
  ArrowLeft, 
  Calendar, 
  Clock, 
  Users, 
  Image, 
  Video, 
  X, 
  Edit2, 
  Save, 
  Trash2, 
  Plus,
  AlertCircle,
  CheckCircle,
  XCircle,
  Film,
  LayoutGrid,
  FileText
} from 'lucide-react';
import './PostDetail.css';

const PostDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);
  
  // Edit mode states
  const [isEditMode, setIsEditMode] = useState(false);
  const [editData, setEditData] = useState({
    caption: '',
    ai_caption: false,
    schedule_datetime: ''
  });
  const [newFiles, setNewFiles] = useState([]);
  const [deletedMediaUrls, setDeletedMediaUrls] = useState([]);
  const [saving, setSaving] = useState(false);

  // ✅ NEW: Post type icons
  const postTypeIcons = {
    image: <Image size={20} />,
    video: <Film size={20} />,
    reel: <Video size={20} />,
    carousel: <LayoutGrid size={20} />,
    text: <FileText size={20} />,
    story: <Film size={20} />
  };

  // ✅ NEW: Status colors
  const getNewStatusColor = (status) => {
    switch (status) {
      case 'READY': return '#6c757d';
      case 'PROCESSING': return '#ffc107';
      case 'WAITING': return '#17a2b8';
      case 'DONE': return '#28a745';
      case 'FAILED': return '#dc3545';
      case 'FAILED_LATE': return '#d9534f';
      case 'CANCELLED': return '#6c757d';
      case 'MANUAL_DONE': return '#5cb85c';
      default: return '#6c757d';
    }
  };

  // ✅ NEW: Status icons
  const getStatusIcon = (status) => {
    switch (status) {
      case 'DONE':
      case 'MANUAL_DONE':
        return <CheckCircle size={18} />;
      case 'FAILED':
      case 'FAILED_LATE':
        return <XCircle size={18} />;
      case 'PROCESSING':
      case 'WAITING':
        return <Clock size={18} />;
      default:
        return null;
    }
  };

  useEffect(() => {
    fetchPostDetails();
  }, [id]);

  const fetchPostDetails = async () => {
    try {
      setLoading(true);
      const response = await postsAPI.getPostById(id);
      if (response.success) {
        setPost(response.post);
        setEditData({
          caption: response.post.caption || '',
          ai_caption: response.post.ai_caption === 1,
          schedule_datetime: formatDateTimeForInput(response.post.schedule_datetime)
        });
      } else {
        setError('Failed to load post');
      }
    } catch (err) {
      console.error('Error fetching post:', err);
      setError('Failed to load post details');
    } finally {
      setLoading(false);
    }
  };

  const parseUtcFromDb = (dt) => {
    if (!dt) return null;
    const iso = dt.includes('T') ? dt : dt.replace(' ', 'T') + 'Z';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d;
  };

  const formatDateTime = (dt) => {
    const d = parseUtcFromDb(dt);
    if (!d) return 'Invalid Date';
    
    const userTimezone = user?.timezone || 'UTC';
    
    return new Intl.DateTimeFormat('en-US', {
      timeZone: userTimezone,
      year: 'numeric',
      month: 'long',
      day: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).format(d);
  };

  const formatDateTimeForInput = (dt) => {
    const d = parseUtcFromDb(dt);
    if (!d) return '';
    
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: user?.timezone || 'UTC',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    
    const parts = formatter.formatToParts(d);
    const year = parts.find(p => p.type === 'year').value;
    const month = parts.find(p => p.type === 'month').value;
    const day = parts.find(p => p.type === 'day').value;
    const hour = parts.find(p => p.type === 'hour').value;
    const minute = parts.find(p => p.type === 'minute').value;
    
    return `${year}-${month}-${day}T${hour}:${minute}`;
  };

  const getPlatformColor = (platform) => {
    const colors = {
      instagram: '#E4405F',
      facebook: '#1877F2',
      youtube: '#FF0000',
      linkedin: '#0A66C2',
      twitter: '#1DA1F2'
    };
    return colors[platform] || '#6c757d';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'uploading': return '#ffc107';
      case 'completed': return '#28a745';
      case 'failed': return '#dc3545';
      case 'scheduled': return '#17a2b8';
      case 'ready': return '#6c757d';
      default: return '#6c757d';
    }
  };

  const isVideo = (url) => {
    return url?.match(/\.(mp4|mov|avi|wmv|flv|mkv|webm)$/i);
  };

  const isMultiMediaPlatform = () => {
    return post?.platform === 'instagram' || post?.platform === 'facebook';
  };

  const canEdit = () => {
    return post?.upload_status !== 'completed' && post?.status !== 'DONE';
  };

  const openLightbox = (index) => {
    setSelectedMediaIndex(index);
    setShowLightbox(true);
  };

  const closeLightbox = () => {
    setShowLightbox(false);
  };

  const navigateMedia = (direction) => {
    const currentMedia = getCurrentMediaUrls();
    if (!currentMedia) return;
    const newIndex = direction === 'next'
      ? (selectedMediaIndex + 1) % currentMedia.length
      : (selectedMediaIndex - 1 + currentMedia.length) % currentMedia.length;
    setSelectedMediaIndex(newIndex);
  };

  const getCurrentMediaUrls = () => {
    if (!post?.media_url) return [];
    const mediaUrls = Array.isArray(post.media_url) ? post.media_url : [post.media_url];
    return mediaUrls.filter(url => !deletedMediaUrls.includes(url));
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const currentMediaCount = getCurrentMediaUrls().length;
    const newFilesCount = newFiles.length;
    const totalCount = currentMediaCount + newFilesCount + files.length;

    if (!isMultiMediaPlatform() && totalCount > 1) {
      alert('Only Instagram and Facebook support multiple media. This platform only supports 1 media file.');
      return;
    }

    if (totalCount > 10) {
      alert('Maximum 10 media files allowed');
      return;
    }

    setNewFiles([...newFiles, ...files]);
  };

  const handleRemoveNewFile = (index) => {
    setNewFiles(newFiles.filter((_, i) => i !== index));
  };

  const handleDeleteMedia = (url) => {
    const currentMedia = getCurrentMediaUrls();
    const newFilesCount = newFiles.length;
    
    if (currentMedia.length + newFilesCount <= 1 && post.platform !== 'linkedin') {
      alert('At least one media file is required');
      return;
    }

    if (!isMultiMediaPlatform()) {
      alert('Cannot delete the only media file for this platform');
      return;
    }

    setDeletedMediaUrls([...deletedMediaUrls, url]);
  };

  const handleUndoDelete = (url) => {
    setDeletedMediaUrls(deletedMediaUrls.filter(u => u !== url));
  };

  const handleInputChange = (e) => {
    const { name, type, value, checked } = e.target;
    setEditData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const totalMedia = getCurrentMediaUrls().length + newFiles.length;
      if (totalMedia === 0 && post.platform !== 'linkedin') {
        alert('At least one media file is required');
        setSaving(false);
        return;
      }
      
      const formData = new FormData();
      formData.append('caption', editData.caption || '');
      formData.append('ai_caption', editData.ai_caption ? 'true' : 'false');
      formData.append('schedule_datetime', editData.schedule_datetime);
      formData.append('timezone', user?.timezone || 'UTC');
      
      newFiles.forEach(file => {
        formData.append('media', file);
      });
      
      if (deletedMediaUrls.length > 0) {
        formData.append('deleted_media', JSON.stringify(deletedMediaUrls));
      }

      console.log('Sending update request...');
      console.log('Caption:', editData.caption);
      console.log('AI Caption:', editData.ai_caption);
      console.log('Schedule:', editData.schedule_datetime);
      console.log('New files:', newFiles.length);
      console.log('Deleted URLs:', deletedMediaUrls.length);

      const response = await postsAPI.updatePost(id, formData);
      
      if (response.success) {
        alert('✅ Post updated successfully!');
        setIsEditMode(false);
        setNewFiles([]);
        setDeletedMediaUrls([]);
        await fetchPostDetails();
      } else {
        alert('❌ Failed to update post: ' + response.message);
      }
    } catch (err) {
      console.error('Error updating post:', err);
      alert('❌ Failed to update post: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePost = async () => {
    if (!window.confirm('⚠️ Are you sure you want to delete this post? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await postsAPI.deletePost(id);
      if (response.success) {
        alert('✅ Post deleted successfully!');
        navigate('/dashboard');
      } else {
        alert('❌ Failed to delete post');
      }
    } catch (err) {
      console.error('Error deleting post:', err);
      alert('❌ Failed to delete post');
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setNewFiles([]);
    setDeletedMediaUrls([]);
    setEditData({
      caption: post.caption || '',
      ai_caption: post.ai_caption === 1,
      schedule_datetime: formatDateTimeForInput(post.schedule_datetime)
    });
  };

  if (loading) {
    return (
      <div className="post-detail-container">
        <div className="loading-spinner">Loading post details...</div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="post-detail-container">
        <div className="error-message">{error || 'Post not found'}</div>
        <button className="btn-back" onClick={() => navigate('/dashboard')}>
          <ArrowLeft size={18} /> Back to Dashboard
        </button>
      </div>
    );
  }

  const currentMedia = getCurrentMediaUrls();
  const mediaCount = currentMedia.length + newFiles.length;
  
  // ✅ NEW: Check if post has errors
  const hasError = post.status === 'FAILED' || post.status === 'FAILED_LATE';

  return (
    <div className="post-detail-container">
      {/* Header */}
      <div className="post-detail-header">
        <button className="btn-back" onClick={() => navigate('/dashboard')}>
          <ArrowLeft size={18} /> Back to Dashboard
        </button>
        <h1>{post.name}</h1>
        <div className="header-actions">
          {canEdit() && !isEditMode && (
            <button className="btn-edit" onClick={() => setIsEditMode(true)}>
              <Edit2 size={16} /> Edit Post
            </button>
          )}
          {canEdit() && (
            <button className="btn-delete" onClick={handleDeletePost}>
              <Trash2 size={16} /> Delete
            </button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="post-detail-content">
        {/* Left Side - Media Gallery */}
        <div className="media-section">
          <div className="media-header">
            <h2>Media Files ({mediaCount})</h2>
            {isEditMode && isMultiMediaPlatform() && mediaCount < 10 && (
              <label className="btn-add-media">
                <Plus size={16} /> Add Media
                <input
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
              </label>
            )}
          </div>

          {mediaCount > 0 ? (
            <div className="media-grid">
              {currentMedia.map((url, index) => (
                <div key={`existing-${index}`} className="media-item">
                  {isVideo(url) ? (
                    <div className="video-thumbnail" onClick={() => openLightbox(index)}>
                      <Video size={40} />
                      <video src={url} />
                    </div>
                  ) : (
                    <img src={url} alt={`Media ${index + 1}`} onClick={() => openLightbox(index)} />
                  )}
                  {isEditMode && isMultiMediaPlatform() && (
                    <button
                      className="btn-delete-media"
                      onClick={() => handleDeleteMedia(url)}
                      title="Delete this media"
                    >
                      <X size={16} />
                    </button>
                  )}
                  <div className="media-overlay" onClick={() => openLightbox(index)}>
                    <span>Click to view</span>
                  </div>
                </div>
              ))}

              {isEditMode && deletedMediaUrls.map((url, index) => (
                <div key={`deleted-${index}`} className="media-item deleted">
                  {isVideo(url) ? (
                    <div className="video-thumbnail">
                      <Video size={40} />
                      <video src={url} />
                    </div>
                  ) : (
                    <img src={url} alt="Deleted" />
                  )}
                  <div className="deleted-overlay">
                    <span>Marked for deletion</span>
                    <button className="btn-undo" onClick={() => handleUndoDelete(url)}>
                      Undo
                    </button>
                  </div>
                </div>
              ))}

              {newFiles.map((file, index) => (
                <div key={`new-${index}`} className="media-item new">
                  <img src={URL.createObjectURL(file)} alt={`New ${index + 1}`} />
                  <button
                    className="btn-delete-media"
                    onClick={() => handleRemoveNewFile(index)}
                    title="Remove this file"
                  >
                    <X size={16} />
                  </button>
                  <div className="new-badge">NEW</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-media-message" style={{ 
              padding: '40px', 
              textAlign: 'center', 
              color: '#666',
              background: '#f8f9fa',
              borderRadius: '8px'
            }}>
              <FileText size={48} style={{ opacity: 0.3, marginBottom: '12px' }} />
              <p>This is a text-only post (no media)</p>
            </div>
          )}
        </div>

        {/* Right Side - Post Details */}
        <div className="details-section">
          <h2>Post Details</h2>

          <div className="detail-card">
            {/* ✅ NEW: Show both status fields with icons */}
            <div className="detail-row" style={{ marginBottom: '16px' }}>
              <span className="detail-label">Status:</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {post.status && post.status !== 'READY' && (
                  <span
                    className="status-badge"
                    style={{ 
                      backgroundColor: getNewStatusColor(post.status),
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      width: 'fit-content'
                    }}
                  >
                    {getStatusIcon(post.status)}
                    {post.status}
                  </span>
                )}
                <span
                  className="status-badge"
                  style={{ 
                    backgroundColor: getStatusColor(post.upload_status),
                    width: 'fit-content'
                  }}
                >
                  {post.upload_status}
                </span>
              </div>
            </div>

            {!canEdit() && (
              <div className="warning-message" style={{ marginBottom: '16px' }}>
                ⚠️ This post is completed and cannot be edited
              </div>
            )}

            {/* ✅ NEW: Show error details if failed */}
            {hasError && (
              <div 
                className="error-details"
                style={{
                  marginBottom: '20px',
                  padding: '16px',
                  background: '#fff3cd',
                  border: '2px solid #ffc107',
                  borderRadius: '8px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
                  <AlertCircle size={24} style={{ color: '#856404', flexShrink: 0 }} />
                  <div>
                    <h3 style={{ margin: '0 0 8px 0', color: '#856404', fontSize: '16px' }}>
                      Post Failed
                    </h3>
                    {post.last_error_message && (
                      <p style={{ margin: '0 0 8px 0', color: '#856404' }}>
                        <strong>Error:</strong> {post.last_error_message}
                      </p>
                    )}
                    {post.last_error_code && (
                      <p style={{ margin: '0', fontSize: '13px', color: '#856404', opacity: 0.9 }}>
                        <strong>Error Code:</strong> {post.last_error_code}
                      </p>
                    )}
                    {post.attempt_count > 0 && (
                      <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#856404' }}>
                        <strong>Retry Attempts:</strong> {post.attempt_count}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="detail-row">
              <span className="detail-label">Platform:</span>
              <span
                className="platform-badge"
                style={{
                  backgroundColor: getPlatformColor(post.platform) + '20',
                  color: getPlatformColor(post.platform)
                }}
              >
                {post.platform?.charAt(0).toUpperCase() + post.platform?.slice(1)}
              </span>
            </div>

            {/* ✅ NEW: Show post_type */}
            {post.post_type && (
              <div className="detail-row">
                <span className="detail-label">Post Type:</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {postTypeIcons[post.post_type]}
                  {post.post_type.charAt(0).toUpperCase() + post.post_type.slice(1)}
                </span>
              </div>
            )}

            {/* ✅ NEW: Show priority if HIGH */}
            {post.priority === 'HIGH' && (
              <div className="detail-row">
                <span className="detail-label">Priority:</span>
                <span style={{ color: '#dc3545', fontWeight: '600' }}>
                  🔴 {post.priority}
                </span>
              </div>
            )}

            <div className="detail-row">
              <Calendar size={18} />
              <span className="detail-label">Scheduled:</span>
              {isEditMode ? (
                <input
                  type="datetime-local"
                  name="schedule_datetime"
                  value={editData.schedule_datetime}
                  onChange={handleInputChange}
                  className="edit-input"
                />
              ) : (
                <span>{formatDateTime(post.schedule_datetime)}</span>
              )}
            </div>

            <div className="detail-row">
              <Clock size={18} />
              <span className="detail-label">Created:</span>
              <span>{formatDateTime(post.created_at)}</span>
            </div>

            {/* ✅ NEW: Show updated_at if different from created_at */}
            {post.updated_at && post.updated_at !== post.created_at && (
              <div className="detail-row">
                <Clock size={18} />
                <span className="detail-label">Last Updated:</span>
                <span>{formatDateTime(post.updated_at)}</span>
              </div>
            )}

            {post.client_id && (
              <div className="detail-row">
                <Users size={18} />
                <span className="detail-label">Client ID:</span>
                <span>{post.client_id}</span>
              </div>
            )}

            <div className="detail-row caption-row">
              <span className="detail-label">Caption:</span>
              {isEditMode ? (
                <textarea
                  name="caption"
                  value={editData.caption}
                  onChange={handleInputChange}
                  className="edit-textarea"
                  rows="4"
                  placeholder="Enter caption..."
                />
              ) : (
                <p className="caption-text">{post.caption || 'No caption'}</p>
              )}
            </div>

            <div className="detail-row">
              <span className="detail-label">AI Generated:</span>
              {isEditMode ? (
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="ai_caption"
                    checked={editData.ai_caption}
                    onChange={handleInputChange}
                  />
                  <span>Yes</span>
                </label>
              ) : (
                <span>{post.ai_caption ? 'Yes' : 'No'}</span>
              )}
            </div>

            {/* ✅ NEW: Show manual note if exists */}
            {post.manual_note && (
              <div 
                className="manual-note"
                style={{
                  marginTop: '16px',
                  padding: '12px',
                  background: '#d1ecf1',
                  border: '1px solid #bee5eb',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: '#0c5460'
                }}
              >
                <strong>📝 Manual Note:</strong>
                <p style={{ margin: '8px 0 0 0' }}>{post.manual_note}</p>
              </div>
            )}

            {isEditMode && (
              <div className="edit-actions">
                <button
                  className="btn-save"
                  onClick={handleSave}
                  disabled={saving}
                >
                  <Save size={16} />
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  className="btn-cancel"
                  onClick={handleCancelEdit}
                  disabled={saving}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      {showLightbox && currentMedia.length > 0 && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <button className="lightbox-close" onClick={closeLightbox}>
            <X size={24} />
          </button>

          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            {isVideo(currentMedia[selectedMediaIndex]) ? (
              <video
                src={currentMedia[selectedMediaIndex]}
                controls
                autoPlay
                className="lightbox-media"
              />
            ) : (
              <img
                src={currentMedia[selectedMediaIndex]}
                alt={`Media ${selectedMediaIndex + 1}`}
                className="lightbox-media"
              />
            )}

            {currentMedia.length > 1 && (
              <>
                <button
                  className="lightbox-nav prev"
                  onClick={() => navigateMedia('prev')}
                >
                  ‹
                </button>
                <button
                  className="lightbox-nav next"
                  onClick={() => navigateMedia('next')}
                >
                  ›
                </button>
                <div className="lightbox-counter">
                  {selectedMediaIndex + 1} / {currentMedia.length}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PostDetail;